import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * LeftSidebarMobile Component
 * 
 * Mobile version of the left sidebar as a drawer.
 * Features:
 * - Slide-in drawer from left
 * - Swipe-to-close gesture
 * - Backdrop click to close
 * - Body scroll lock when open
 */
const LeftSidebarMobile = ({
  isOpen = false,
  onClose,
  onNewChat,
  sessions = [],
  currentSessionId,
  onSelectSession,
  userName = 'User',
  onProfileClick,
  theme = 'dark',
  onDeleteSession
}) => {
  const navigate = useNavigate();

  // Lock body scroll when drawer is open
  useBodyScrollLock(isOpen);

  const chats = sessions;

  const handleNewChatClick = () => {
    onNewChat?.();
    onClose();
  };

  const handleProfileClick = () => {
    onProfileClick?.();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: 'linear'
            }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-[200]"
            data-framer-motion="true"
            style={{
              willChange: 'opacity',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
              pointerEvents: 'auto',
            }}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{
              duration: 0.25,
              ease: 'easeOut'
            }}
            className="fixed top-0 left-0 h-screen flex flex-col z-[201]"
            data-framer-motion="true"
            style={{
              width: 'min(85vw, 320px)',
              paddingLeft: 'env(safe-area-inset-left, 0px)', /* MOBILE: Respect safe area on left edge */
              background: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(248, 249, 250, 0.95)',
              backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
              WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
              borderRight: theme === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.08)'
                : '1px solid rgba(148, 163, 184, 0.6)',
              padding: '12px',
              paddingTop: 'calc(12px + env(safe-area-inset-top))',
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.45)',
              willChange: 'transform',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              isolation: 'isolate',
              contain: 'layout style paint',
              pointerEvents: 'auto',
            }}
            data-theme={theme}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between mb-3">
              <div
                className="logo-title text-[20px] font-bold cursor-pointer"
                style={{
                  color: theme === 'dark' ? '#ffffff' : '#0f172a',
                }}
                onClick={() => {
                  navigate('/');
                  onClose();
                }}
              >
                <span className="lawnance-brand">LAWNANCE</span>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg cursor-pointer transition-all duration-300 active:scale-95"
                aria-label="Close sidebar"
                style={{
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme === 'dark' ? 'rgba(30, 30, 30, 0.9)' : 'rgba(240, 240, 240, 0.9)',
                  color: theme === 'dark' ? '#ffffff' : '#0f172a',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* New Chat Button */}
            <button
              className="new-chat-btn w-full border-none rounded-full flex items-center justify-center cursor-pointer mb-3 active:scale-95 transition-transform"
              onClick={handleNewChatClick}
              style={{
                minHeight: '48px',
                background: theme === 'dark' ? 'rgba(20, 20, 20, 0.8)' : 'rgba(240, 240, 240, 0.8)',
                padding: '14px 12px',
                gap: '6px',
                color: theme === 'dark' ? '#ffffff' : '#0f172a',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: theme === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(15, 23, 42, 0.1)',
              }}
            >
              <span className="new-chat-icon flex items-center justify-center w-[18px] h-[18px] flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 2V16M2 9H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <span className="whitespace-nowrap">New Chat</span>
            </button>

            {/* Recent Section Title */}
            <div
              className="sidebar-section-title text-[11px] uppercase mb-3 whitespace-nowrap"
              style={{
                letterSpacing: '0.15em',
                color: theme === 'dark' ? '#9ca3af' : '#475569',
              }}
            >
              RECENT
            </div>

            {/* Recent List */}
            <ul className="recent-list flex-1 overflow-y-auto mb-3 list-none">
              {chats.map((chat) => {
                const isActive = chat.id === currentSessionId;
                const activeBg = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.6)';

                return (
                  <li
                    key={chat.id}
                    className="cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis active:scale-95 transition-transform"
                    style={{
                      padding: '12px 8px',
                      minHeight: '44px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: isActive ? (theme === 'dark' ? '#ffffff' : '#0f172a') : (theme === 'dark' ? '#888888' : '#1e293b'),
                      background: isActive ? activeBg : 'transparent',
                      fontWeight: isActive ? 600 : 400,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => {
                      onSelectSession?.(chat.id);
                      onClose();
                    }}
                  >
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {chat.title || 'New Chat'}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteSession?.(chat.id);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '4px',
                        marginLeft: '8px',
                        cursor: 'pointer',
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        minWidth: '24px',
                        minHeight: '24px'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Sidebar Footer */}
            <div
              className="sidebar-footer flex items-center cursor-pointer rounded-lg active:scale-95 transition-transform"
              style={{
                gap: '8px',
                padding: '12px 8px',
                minHeight: '56px',
                borderTop: theme === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(203, 213, 225, 0.8)',
                marginTop: '12px',
              }}
              onClick={handleProfileClick}
            >
              {/* User Avatar */}
              <div
                className="user-avatar w-8 h-8 rounded-full flex-shrink-0 relative"
                style={{
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M3 14C3 11 5.5 9 8 9C10.5 9 13 11 13 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>

              {/* User Name */}
              <span
                className="user-name text-[13px] whitespace-nowrap"
                style={{
                  color: theme === 'dark' ? '#ffffff' : '#0f172a',
                }}
              >
                {userName}
              </span>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LeftSidebarMobile;

